import {
  Container,
  Box,
  Drawer,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton, styled, useTheme, Avatar, Tooltip, Collapse, Theme
} from "@mui/material";
import {useEffect} from "react";
import {Outlet, useNavigate, useLocation} from "react-router-dom";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {ResponsiveAppBar} from "@/components/layout/AppHeader.tsx";
import {ConfirmDialogProvider} from "@/hooks/ConfirmDialogProvider.tsx";
import {AppFooter} from "@/components/layout/AppFooter";
import {useUserData} from "@/hooks/use-user-data-provider.ts";
import {
  BarChart,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Home,
  LogOut,
  Mail,
  MonitorCheck,
  Shield,
  User,
  Users,
} from "lucide-react";
import {useMemo, useState} from "react";
import {Permission} from "@/utils/permissions.ts";
import {useGetPendingReviewCountQuery} from "@/graphql/graphql.ts";
import {useAuth0} from "@auth0/auth0-react";

const DrawerHeader = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const drawerWidth = 240;
const miniDrawerWidth = 65;

// メニュー項目の型定義
interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path: string | null;
  tooltip?: string;
  suffixIcon: React.ReactNode;
  onClick?: () => void;
  subMenu?: MenuItem[];
}

const openedMixin = (theme: Theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `${miniDrawerWidth}px`,
});

export function Layout() {
  const [open, setOpen] = useState(true);
  const theme = useTheme()
  const location = useLocation();
  
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  
  const handleDrawerClose = () => {
    setOpen(false);
  };
  const nav = useNavigate()
  
  const user = useUserData()
  const {logout} = useAuth0()
  
  // 特定のパターンのURLに遷移した時、自動でDrawerを閉じる
  useEffect(() => {
    // /zyusetu/***/edit パターン（編集ページ）
    const isEditPage = /\/zyusetu\/.*\/edit/.test(location.pathname);
    
    // /zyusetu/reviews/* パターン（レビューページ）
    const isReviewPage = location.pathname.startsWith('/zyusetu/reviews/');
    
    if (isEditPage || isReviewPage) {
      handleDrawerClose();
    }
  }, [location.pathname]);
  
  type MenuSection = MenuItem[] | { divider: boolean };
  
  const menus = useMemo((): (MenuSection | false | undefined)[] => [
    [
      {
        title: "重説一覧",
        icon: <Home/>,
        path: "/",
        suffixIcon: null,
        tooltip: undefined
      },
    ],
    {
      divider: true
    },
    user.user?.organization?.useApprovalFlow && user.permissions.includes(Permission.WriteApprove) ? [
      {
        title: "承認待ち",
        icon: <Shield/>,
        path: "zyusetu/reviews",
        tooltip: "あなたの承認待ちの文書を確認します",
        suffixIcon: <PendingApprovalCount/>
      },
    ] : false,
    user.permissions.includes(Permission.WriteUsers) ? [{
      title: "ユーザー管理画面",
      icon: <Users/>,
      path: "admin",
      tooltip: "ユーザー管理画面",
      suffixIcon: null
    }] : false,
    user.permissions.includes(Permission.WriteUsers) ? [{
      title: "承認フロー管理画面",
      icon: <MonitorCheck/>,
      path: "admin/approvalFlow",
      tooltip: "承認フロー管理画面",
      suffixIcon: null
    }] : false,
    user.user ? {
      divider: true
    } : false,
    user.user ? [{
      title: "利用状況確認",
      icon: <BarChart/>,
      path: "/usage",
      tooltip: "利用状況を確認します",
      suffixIcon: null
    }] : false,
    // お問い合わせメニュー項目
    [{
      title: "お問い合わせ",
      icon: <Mail/>,
      path: "/contact",
      tooltip: "お問い合わせページへ",
      suffixIcon: null
    }],
    user.user ? [{
      title: user.user?.name || "ユーザー",
      icon: <User/>,
      path: null,
      tooltip: "ユーザー設定",
      suffixIcon: null,
      subMenu: [
        {
          title: "ログアウト",
          icon: <LogOut/>,
          path: null,
          tooltip: "ログアウトします",
          suffixIcon: null,
          onClick: () => logout()
        }
      ]
    }] : false
  ], [user.permissions, user.user?.organization?.useApprovalFlow, user.user, logout])
  
  return (
    <Box sx={{minHeight: "100vh", display: "flex", flexDirection: "column"}}>
      <ResponsiveAppBar open={open}/>
      <Container maxWidth={false} sx={{mt: 3, mb: 10}}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ConfirmDialogProvider>
            <Drawer
              sx={{
                width: open ? drawerWidth : miniDrawerWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '& .MuiDrawer-paper': {
                  width: open ? drawerWidth : miniDrawerWidth,
                  boxSizing: 'border-box',
                  ...(open && openedMixin(theme)),
                  ...(!open && closedMixin(theme)),
                },
              }}
              variant="permanent"
              anchor="left"
              open={open}
            >
              <DrawerHeader>
                <IconButton onClick={open ? handleDrawerClose : handleDrawerOpen}>
                  {open
                    ? (theme.direction === 'ltr' ? <ChevronLeftIcon/> : <ChevronRightIcon/>)
                    : (theme.direction === 'ltr' ? <ChevronRightIcon/> : <ChevronLeftIcon/>)
                  }
                </IconButton>
              </DrawerHeader>
              <Divider sx={{mb: 2}}/>
              {
                menus.filter(Boolean).map((menu, index) => {
                  if (Array.isArray(menu)) {
                    return <List key={`nav-item-${index}`}>
                      {menu.map((item: MenuItem) => (
                        <MenuItemWithSubmenu
                          key={item.title}
                          item={item}
                          handleNavigate={nav}
                          drawerOpen={open}
                        />
                      ))}
                    </List>
                  } else if (menu && menu?.divider) {
                    return <Divider key={`nav-divider-${index}`} sx={{my: 2}}/>
                  }
                })}
            </Drawer>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                marginLeft: open ? `${drawerWidth}px` : `${miniDrawerWidth}px`,
                transition: theme => theme.transitions.create('margin', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
              }}
            >
              <Outlet/>
            </Box>
          </ConfirmDialogProvider>
        </LocalizationProvider>
      </Container>
      <AppFooter/>
    </Box>
  );
}

function PendingApprovalCount() {
  const {data: count} = useGetPendingReviewCountQuery({}, {
    refetchOnWindowFocus: true,
    refetchOnMount: true
  })
  return <Avatar
    sx={{
      bgcolor: 'warning.main',
      width: "25px",
      height: "25px",
      fontSize: "1rem",
      fontWeight: "bold"
    }}>{count?.getPendingReviewCount}</Avatar>
}

interface MenuItemWithSubmenuProps {
  item: MenuItem;
  handleNavigate: (path: string) => void;
  drawerOpen: boolean;
}

function MenuItemWithSubmenu({item, handleNavigate, drawerOpen}: MenuItemWithSubmenuProps) {
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  
  const handleClick = () => {
    if (item.subMenu) {
      setSubMenuOpen(!subMenuOpen);
    } else if (item.path) {
      handleNavigate(item.path);
    } else if (item.onClick) {
      item.onClick();
    }
  };
  
  return (
    <>
      <ListItem disablePadding>
        <Tooltip title={item.tooltip ?? ""} placement="right">
          <ListItemButton onClick={handleClick}
                          sx={{minHeight: 48, justifyContent: drawerOpen ? 'initial' : 'center', px: 2.5}}>
            <ListItemIcon sx={{minWidth: 0, mr: drawerOpen ? 3 : 'auto', justifyContent: 'center'}}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.title} sx={{opacity: drawerOpen ? 1 : 0}}/>
            {drawerOpen && item.suffixIcon && item.suffixIcon}
            {drawerOpen && item.subMenu && (
              subMenuOpen ? <ChevronUpIcon size={16}/> : <ChevronDownIcon size={16}/>
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
      {item.subMenu && (
        <Collapse in={subMenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subMenu.map((subItem) => (
              <ListItem key={subItem.title} disablePadding>
                <Tooltip title={subItem.tooltip ?? ""} placement="right">
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      pl: drawerOpen ? 4 : 2.5
                    }}
                    onClick={() => {
                      if (subItem.path) {
                        handleNavigate(subItem.path);
                      } else if (subItem.onClick) {
                        subItem.onClick();
                      }
                    }}
                  >
                    <ListItemIcon sx={{minWidth: 0, mr: drawerOpen ? 3 : 'auto', justifyContent: 'center'}}>
                      {subItem.icon}
                    </ListItemIcon>
                    <ListItemText primary={subItem.title} sx={{opacity: drawerOpen ? 1 : 0}}/>
                    {drawerOpen && subItem.suffixIcon && subItem.suffixIcon}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}
